/**
 * AppICEMAP — Push Notification Service
 *
 * Manages Web Push subscription lifecycle:
 *   1. Request notification permission (with pre-prompt context)
 *   2. Subscribe to push service via service worker
 *   3. Save subscription to Directus user profile
 *   4. Handle incoming push events
 *   5. In-app notification badge (fallback when push not supported)
 *
 * VAPID keys should be set in environment or generated.
 * For development, a placeholder is used.
 */

import { getClient, isAuthenticated } from "../api/directus.js";
import { updateItem } from "@directus/sdk";

/**
 * VAPID public key (placeholder — generate real key for production).
 * Generate with: npx web-push generate-vapid-keys
 */
const VAPID_PUBLIC_KEY =
	import.meta.env.VITE_VAPID_PUBLIC_KEY ||
	"BDd3_hVL9fZi9Ybo2UUixrqFtEfKkTnGeA9JmFkO0YcHTiVLpWYBs7PKXvGQoQ-YZnM7BVFYLaDwHlHkZwAX0GM";

/** Key for storing subscription status in localStorage */
const PUSH_STORAGE_KEY = "appicemap-push-enabled";

/* ── Permission & Pre-prompt ──────────────────────────────────────────────── */

/**
 * Check current notification permission status.
 * @returns {'granted' | 'denied' | 'default'}
 */
export function getPermissionState() {
	if (!("Notification" in window)) return "denied";
	return Notification.permission;
}

/**
 * Check if push messaging is supported by the browser.
 */
export function isPushSupported() {
	return (
		"serviceWorker" in navigator &&
		"PushManager" in window &&
		"Notification" in window
	);
}

/**
 * Show a pre-prompt dialog explaining why push notifications are useful
 * before triggering the browser's native permission prompt.
 *
 * @returns {Promise<boolean>} true if user accepted the pre-prompt
 */
export async function requestPermissionWithContext() {
	if (!isPushSupported()) return false;

	const state = getPermissionState();
	if (state === "granted") return true;
	if (state === "denied") return false;

	// Pre-prompt context — explain before native prompt
	return new Promise((resolve) => {
		const dialog = document.createElement("div");
		dialog.className = "push-pre-prompt";
		dialog.setAttribute("role", "dialog");
		dialog.setAttribute("aria-modal", "true");
		dialog.innerHTML = `
          <div class="push-pre-prompt__card">
            <i class="ph ph-bell-ringing push-pre-prompt__icon" aria-hidden="true"></i>
            <h3>Alertas de ICE cercano</h3>
            <p>
              Recibe notificaciones cuando se reporte actividad de ICE o alertas
              de seguridad cerca de tu ubicación. También te avisaremos cuando
              confirmen tus reportes.
            </p>
            <div class="push-pre-prompt__actions">
              <button class="btn btn--ghost" data-action="deny">Ahora no</button>
              <button class="btn btn--primary" data-action="allow">Activar alertas</button>
            </div>
          </div>
        `;

		dialog
			.querySelector('[data-action="deny"]')
			.addEventListener("click", () => {
				dialog.remove();
				resolve(false);
			});

		dialog
			.querySelector('[data-action="allow"]')
			.addEventListener("click", async () => {
				dialog.remove();

				// Now trigger browser prompt
				try {
					const result = await Notification.requestPermission();
					resolve(result === "granted");
				} catch {
					resolve(false);
				}
			});

		// Click outside to dismiss
		dialog.addEventListener("click", (e) => {
			if (e.target === dialog) {
				dialog.remove();
				resolve(false);
			}
		});

		document.body.appendChild(dialog);
	});
}

/* ── Subscription ─────────────────────────────────────────────────────────── */

/**
 * Subscribe to push notifications.
 * Saves the subscription to the Directus user profile.
 *
 * @returns {Promise<PushSubscription | null>}
 */
export async function subscribeToPush() {
	if (!isPushSupported()) return null;

	try {
		const registration = await navigator.serviceWorker.ready;

		// Check existing subscription
		let subscription = await registration.pushManager.getSubscription();

		if (!subscription) {
			subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
			});
		}

		// Save to backend if user is authenticated
		if (isAuthenticated()) {
			await saveSubscriptionToServer(subscription);
		}

		// Persist preference
		try {
			localStorage.setItem(PUSH_STORAGE_KEY, "true");
		} catch {
			/* ignore */
		}

		return subscription;
	} catch (err) {
		console.error("[Push] Subscription failed:", err);
		return null;
	}
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush() {
	try {
		const registration = await navigator.serviceWorker.ready;
		const subscription = await registration.pushManager.getSubscription();
		if (subscription) {
			await subscription.unsubscribe();
		}

		// Remove from backend
		if (isAuthenticated()) {
			await removeSubscriptionFromServer();
		}

		try {
			localStorage.removeItem(PUSH_STORAGE_KEY);
		} catch {
			/* ignore */
		}
	} catch (err) {
		console.error("[Push] Unsubscribe error:", err);
	}
}

/**
 * Check if user has enabled push locally.
 */
export function isPushEnabled() {
	try {
		return localStorage.getItem(PUSH_STORAGE_KEY) === "true";
	} catch {
		return false;
	}
}

/* ── Server sync ──────────────────────────────────────────────────────────── */

async function saveSubscriptionToServer(subscription) {
	try {
		const client = getClient();
		const { getSession } = await import("../api/directus.js");
		const session = getSession();
		if (!session) return;

		// Store the subscription string in user profile
		// Directus users have a custom field `push_subscription` (JSON string)
		const subData = subscription.toJSON();

		// Try to get current user ID from session
		const user = await client.request(
			(await import("@directus/sdk")).readMe({ fields: ["id"] }),
		);

		if (user?.id) {
			await client.request(
				updateItem("directus_users", user.id, {
					push_subscription: JSON.stringify(subData),
				}),
			);
		}
	} catch (err) {
		console.warn("[Push] Failed to save subscription to server:", err);
	}
}

async function removeSubscriptionFromServer() {
	try {
		const client = getClient();
		const user = await client.request(
			(await import("@directus/sdk")).readMe({ fields: ["id"] }),
		);

		if (user?.id) {
			await client.request(
				updateItem("directus_users", user.id, {
					push_subscription: null,
				}),
			);
		}
	} catch {
		/* best effort */
	}
}

/* ── In-app notification badge ────────────────────────────────────────────── */

/**
 * In-app fallback badge for platforms without Web Push (iOS).
 * Stores notification count in localStorage and fires an event.
 */

const BADGE_STORAGE_KEY = "appicemap-notif-count";

export function getNotificationCount() {
	try {
		return parseInt(localStorage.getItem(BADGE_STORAGE_KEY) || "0", 10);
	} catch {
		return 0;
	}
}

export function setNotificationCount(count) {
	try {
		localStorage.setItem(BADGE_STORAGE_KEY, String(Math.max(0, count)));
	} catch {
		/* ignore */
	}
	window.dispatchEvent(
		new CustomEvent("notifications:updated", {
			detail: { count: Math.max(0, count) },
		}),
	);
}

export function incrementNotificationCount() {
	const current = getNotificationCount();
	setNotificationCount(current + 1);
}

export function clearNotifications() {
	setNotificationCount(0);
}

/* ── Notification categories ─────────────────────────────────────────────── */

/**
 * Handle an incoming notification click event.
 * Navigate to the relevant report or screen.
 *
 * @param {NotificationEvent | object} event
 */
export function handleNotificationClick(event) {
	const data = event.notification?.data || event.data || {};

	// Clear badge
	clearNotifications();

	// Navigate based on category
	if (data.reportId) {
		// Dynamic import to avoid circular deps
		import("../router.js").then(({ default: router }) => {
			router.navigate(`/detail/${data.reportId}`);
		});
	} else if (data.route) {
		import("../router.js").then(({ default: router }) => {
			router.navigate(data.route);
		});
	}
}

/**
 * Notification categories per the spec:
 *   - ICE nearby: data.category = 'ice-nearby', data.reportId
 *   - Report confirmed/denied: data.category = 'report-vote', data.reportId
 *   - Karma milestone: data.category = 'karma-milestone'
 */

export const NOTIFICATION_CATEGORIES = {
	ICE_NEARBY: "ice-nearby",
	REPORT_VOTE: "report-vote",
	KARMA_MILESTONE: "karma-milestone",
};

/* ── Utility ──────────────────────────────────────────────────────────────── */

/**
 * Convert a base64 string to a Uint8Array for VAPID key.
 */
function urlBase64ToUint8Array(base64String) {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
	const rawData = atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; ++i) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}
